import AcademyPortalAccess from './AcademyPortalAccess';
import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import AcademyEnrollmentDetails from './AcademyEnrollmentDetails';
import { EnrollmentModal } from './AcademyEnrollments';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow, DetailField, fmtMoney } from './AcademyShared';

const STATUSES = ['active', 'idle', 'withdrawn', 'archived'];

export default function AcademyStudents() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [enrolled, setEnrolled] = useState(null);
  const [enrolling, setEnrolling] = useState(null);
  const [cohorts, setCohorts] = useState([]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/academy/students?per_page=50`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status, search]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetch(`${API}/companies/${companyId}/academy/cohorts?per_page=500&status=active`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : { data: [] }).then(j => setCohorts(j.data || [])).catch(() => setCohorts([])); }, [companyId]);

  const changeStatus = async (id, newStatus) => {
    await fetch(`${API}/companies/${companyId}/academy/students/${id}/status`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    fetchList();
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this student?')) return;
    const res = await fetch(`${API}/companies/${companyId}/academy/students/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot delete'); return; }
    fetchList();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🧑‍🎓 Students</h2>
        <button style={btnPrimary} onClick={() => setEditing({})}>+ New Student</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input style={{ ...inputStyle, width: 220 }} placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...inputStyle, width: 160 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Code', 'Name', 'Email', 'Phone', 'Enrollments', 'Total Paid', 'Outstanding Balance', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No students</td></tr>}
              {list.map(s => (
                <tr key={s.id}>
                  <td style={{ ...tdStyle, fontWeight: 600, cursor: 'pointer', color: '#3b82f6' }} onClick={() => setViewing(s)}>{s.student_code}</td>
                  <td style={tdStyle}>{s.full_name}</td>
                  <td style={tdStyle}>{s.email}</td>
                  <td style={tdStyle}>{s.phone || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{s.enrollments_count ?? 0}</td>
                  <td style={tdStyle}>{s.payment_summary ? fmtMoney(s.payment_summary.total_paid) : 'Unavailable'}</td>
                  <td style={{ ...tdStyle, fontWeight: 600, color: !s.payment_summary ? '#6b7280' : Number(s.payment_summary.overdue_amount) > 0 ? '#dc2626' : '#15803d' }}>
                    {s.payment_summary ? <>{fmtMoney(s.payment_summary.outstanding_amount)}<div style={{ fontSize: 11, fontWeight: 400 }}>{Number(s.payment_summary.overdue_amount) > 0 ? `${fmtMoney(s.payment_summary.overdue_amount)} overdue` : 'No overdue'}</div></> : 'Unavailable'}
                  </td>
                  <td style={tdStyle}><Badge value={s.status} /></td>
                  <td style={tdStyle}>
                    <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setViewing(s)}>View</button>
                    <button style={{ ...btnSmall, marginRight: 6, color: '#2563eb' }} onClick={() => setEnrolling(s)}>Enroll</button>
                    <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setEditing(s)}>Edit</button>
                    {s.status === 'archived' && !s.merged_into_id && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(s.id, 'restore')}>Restore</button>}
                    {s.can_delete === false && s.status !== 'archived' && <button style={{ ...btnSmall, marginRight:6 }} onClick={() => changeStatus(s.id, 'archived')}>Archive</button>}
                    <button disabled={s.can_delete === false || s.enrollments_count > 0}
                      title={s.delete_block_reason || (s.enrollments_count > 0 ? 'Student has linked enrollments. Archive instead.' : 'Delete student')}
                      style={{ ...btnSmall, color: '#ef4444', opacity:s.can_delete === false || s.enrollments_count > 0 ? 0.45 : 1, cursor:s.can_delete === false || s.enrollments_count > 0 ? 'not-allowed' : 'pointer' }} onClick={() => removeItem(s.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <StudentModal companyId={companyId} item={editing} onClose={() => setEditing(null)} onSaved={student => { setEditing(null); setEnrolled(student.auto_enrollment || null); fetchList(); }} />}
      {enrolled && <AcademyEnrollmentDetails companyId={companyId} enrollment={enrolled} created onClose={() => setEnrolled(null)} onSaved={fetchList} />}
      {viewing && <StudentDetail companyId={companyId} studentId={viewing.id} onClose={() => setViewing(null)} onChanged={fetchList} />}
      {enrolling && <EnrollmentModal companyId={companyId} students={list} cohorts={cohorts} initialStudent={enrolling} onClose={() => setEnrolling(null)} onSaved={() => { setEnrolling(null); fetchList(); }} />}
    </div>
  );
}

export function StudentModal({ companyId, item, onClose, onSaved }) {
  const isNew = !item.id;
  const [form, setForm] = useState({
    full_name: item.full_name || '', email: item.email || '',
    phone: item.phone || '', date_of_birth: item.date_of_birth?.slice(0,10) || '', address: item.address || '',
    notes: item.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const savingRef = React.useRef(false);
  const [error, setError] = useState('');
  const [idType, setIdType] = useState('');
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [cohortId, setCohortId] = useState('');
  const [cohorts, setCohorts] = useState([]);
  const [cohortError, setCohortError] = useState('');
  const [cohortsLoading, setCohortsLoading] = useState(isNew);
  useEffect(() => {
    if (!isNew) return;
    const controller = new AbortController();
    (async () => {
      try {
        const choices = [];
        let page = 1, last = 1;
        do {
          const response = await fetch(`${API}/companies/${companyId}/academy/cohorts?status=active&per_page=100&page=${page}`, { headers: getAuthHeaders(), signal: controller.signal });
          const data = await response.json();
          if (!response.ok) throw new Error(data.message || 'Cohorts could not be loaded.');
          choices.push(...(data.data || [])); last = Number(data.last_page || 1); page++;
        } while (page <= last);
        if (!controller.signal.aborted) setCohorts(choices.filter(c => c.status === 'active'));
      } catch (failure) { if (!controller.signal.aborted) setCohortError(failure.message); }
      finally { if (!controller.signal.aborted) setCohortsLoading(false); }
    })();
    return () => controller.abort();
  }, [companyId, isNew]);

  const save = async () => {
    if (savingRef.current) return;
    setError('');
    if (!form.full_name.trim()) { setError('Full name is required.'); return; }
    if (idType && (!front || (idType === 'national_id' && !back))) { setError(idType === 'national_id' ? 'Upload both the front and back of the national ID.' : 'Upload the passport identification page.'); return; }
    if ([front,back].some(file => file && file.size > 5 * 1024 * 1024)) { setError('Each ID file must be 5 MB or smaller.'); return; }
    savingRef.current = true; setSaving(true);
    try {
      const url = `${API}/companies/${companyId}/academy/students${isNew ? '' : '/' + item.id}`;
      const body = new FormData();
      Object.entries(form).forEach(([key,value]) => body.append(key,value));
      if (!isNew) body.append('_method','PUT');
      if (idType) { body.append('id_document_type',idType); body.append('id_front',front); if (back && idType === 'national_id') body.append('id_back',back); }
      if (isNew && cohortId) body.append('cohort_id',cohortId);
      const submit = () => fetch(url,{ method:'POST', headers:getAuthHeaders(), body });
      let res = await submit();
      let data = await res.json();
      if (res.status === 409 && data.requires_confirmation) {
        if (!window.confirm(`${data.message} Create a separate student anyway?`)) return;
        body.append('confirm_duplicate','1'); res = await submit(); data = await res.json();
      }
      if (!res.ok) throw new Error(data.errors ? Object.values(data.errors).flat().join(' ') : data.message || 'Save failed.');
      onSaved(data);
    } catch (failure) { setError(failure.message || 'Save failed. Please try again.'); }
    finally { savingRef.current = false; setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={() => { if (!saving) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label={isNew ? 'New Student' : 'Edit Student'} style={{ ...modalStyle, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{isNew ? 'New Student' : 'Edit Student'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormRow label="Full Name *"><input aria-label="Full Name" style={inputStyle} value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} /></FormRow>
          <FormRow label="Email"><input style={inputStyle} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></FormRow>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormRow label="Phone"><input style={inputStyle} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></FormRow>
          <FormRow label="Date of Birth"><input type="date" style={inputStyle} value={form.date_of_birth} onChange={e => setForm({ ...form, date_of_birth: e.target.value })} /></FormRow>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
          <FormRow label="Address"><input style={inputStyle} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></FormRow>
        </div>
        <FormRow label="Notes"><textarea style={{ ...inputStyle, minHeight: 60 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></FormRow>
        <fieldset disabled={saving} style={{ border:'1px solid #e5e7eb', borderRadius:8, padding:12, margin:'12px 0' }}>
          <legend>Identification document</legend>
          {!isNew && item.id_document_type && <p>Current document: {item.id_document_type === 'national_id' ? 'National ID' : 'Passport'}. Leave the selection unchanged to keep it.</p>}
          <FormRow label="ID type">
            <select aria-label="ID type" style={inputStyle} value={idType} onChange={e => { setIdType(e.target.value); setFront(null); setBack(null); }}>
              <option value="">{isNew ? 'No ID upload for now' : 'Keep current ID document'}</option>
              <option value="national_id">National ID — front and back</option>
              <option value="passport">Passport — identification page</option>
            </select>
          </FormRow>
          {idType && <>
            <p>JPG, PNG or PDF. Maximum 5 MB per file. Files are private.</p>
            <FormRow label={idType === 'national_id' ? 'National ID — front *' : 'Passport identification page *'}>
              <input key={idType+'front'} aria-label={idType === 'national_id' ? 'National ID front' : 'Passport identification page'} type="file" accept="image/jpeg,image/png,application/pdf" onChange={e => setFront(e.target.files[0] || null)} />
            </FormRow>
            {idType === 'national_id' && <FormRow label="National ID — back *"><input key={idType+'back'} aria-label="National ID back" type="file" accept="image/jpeg,image/png,application/pdf" onChange={e => setBack(e.target.files[0] || null)} /></FormRow>}
          </>}
        </fieldset>
        {isNew && <FormRow label="Enroll in a cohort">
          <select aria-label="Enroll in a cohort" style={inputStyle} value={cohortId} disabled={saving || cohortsLoading || Boolean(cohortError)} onChange={e=>setCohortId(e.target.value)}>
            <option value="">{cohortsLoading ? 'Loading cohorts…' : 'Do not enroll yet'}</option>
            {cohorts.map(c=><option key={c.id} value={c.id}>{c.name} ({c.cohort_code})</option>)}
          </select>
          {cohortError && <p role="alert">{cohortError} You can save the student without enrollment.</p>}
          {!cohortsLoading && !cohortError && !cohorts.length && <p>No active cohorts available.</p>}
          {cohortId && <p>Saving will enroll this student at the cohort tuition of ${Number(cohorts.find(c=>String(c.id)===cohortId)?.price || 0).toFixed(2)}. Enrollment details will open next.</p>}
        </FormRow>}
        {error && <p role="alert" style={{ color:'#b91c1c' }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : cohortId ? 'Save & enroll' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

export function StudentDetail({ companyId, studentId, onClose, onChanged }) {
  const [student, setStudent] = useState(null);
  const [viewingRegistration, setViewingRegistration] = useState(null), [registrationRevision, setRegistrationRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [error, setError] = useState('');
  const money = value => '$' + Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API}/companies/${companyId}/academy/students/${studentId}`, { headers: getAuthHeaders() });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Student details could not be loaded.');
        setStudent(data);
      } catch (e) { setError(e.message || 'Student details could not be loaded.'); }
      setLoading(false);
    })();
  }, [companyId, studentId, registrationRevision]);

  return (
    <>
    <div style={{ ...overlayStyle, ...(viewingRegistration ? { visibility: 'hidden', pointerEvents: 'none' } : {}) }} aria-hidden={Boolean(viewingRegistration) || undefined} onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Student details" style={{ ...modalStyle, width: 720, maxWidth: '95vw', boxSizing: 'border-box' }} onClick={e => e.stopPropagation()}>
        {error ? <><p role="alert">{error}</p><button style={btnSecondary} onClick={onClose}>Close</button></> : initialLoading || !student ? <div>Loading...</div> : (
          <>
            <h3 style={{ marginTop: 0 }}>{student.full_name} <Badge value={student.status} /></h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
              <DetailField label="Code" value={student.student_code} />
              <DetailField label="Email" value={student.email} />
              <DetailField label="Phone" value={student.phone} />
              <DetailField label="DOB" value={student.date_of_birth} />
              <DetailField label="Address" value={student.address} />
            </div>
            {student.id_document_type && <section style={{ marginBottom:16 }}>
              <h4>Identification — {student.id_document_type === 'national_id' ? 'National ID' : 'Passport'}</h4>
              {['front','back'].filter(side=>student.identity_files?.[side]).map(side=><button key={side} style={{ ...btnSmall, marginRight:8 }} onClick={async()=>{
                try {
                  const response=await fetch(`${API}/companies/${companyId}/academy/students/${studentId}/identity/${side}`,{headers:getAuthHeaders()});
                  if(!response.ok) { const data=await response.json(); throw new Error(data.message || 'Document unavailable.'); }
                  const blob=await response.blob(); const url=URL.createObjectURL(blob);
                  const link=document.createElement('a'); link.href=url; link.download=`student-${studentId}-${side}.${blob.type==='application/pdf'?'pdf':blob.type==='image/png'?'png':'jpg'}`; link.click();
                  setTimeout(()=>URL.revokeObjectURL(url),1000);
                } catch(failure) { alert(failure.message); }
              }}>{student.id_document_type==='passport'?'Download passport page':`Download ${side}`}</button>)}
            </section>}
            <section aria-label="Student payment summary" style={{ background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:8, padding:16, marginBottom:20 }}>
              <h4 style={{ margin:'0 0 12px' }}>Payment summary</h4>
              {student.payment_summary ? <>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:12 }}>
                  <DetailField label="Payment status" value={<Badge value={student.payment_summary.status} />} />
                  <DetailField label="Total amount paid" value={money(student.payment_summary.total_paid)} />
                  <DetailField label="Outstanding amount" value={money(student.payment_summary.outstanding_amount)} />
                  <DetailField label="Overdue amount" value={<span style={{ color:student.payment_summary.overdue_amount > 0 ? '#b91c1c' : undefined }}>{money(student.payment_summary.overdue_amount)}</span>} />
                </div>
                <p style={{ margin:'4px 0 0', fontSize:12, color:'#64748b' }}>Across all enrollments. Reversed payments and waived amounts are excluded. Overdue includes unpaid installments due before {student.payment_summary.as_of_date}; balances without a due date are not marked overdue.</p>
              </> : <p style={{ margin:0 }}>Payment details are unavailable with your current permissions.</p>}
            </section>
            <AcademyPortalAccess role="student" entityId={studentId} companyId={companyId} />
            <h4 style={{ marginBottom: 8 }}>Enrollments</h4>
            <table style={tableStyle}>
              <thead><tr>{['Code', 'Cohort', 'Status', 'Enrolled', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {(student.enrollments || []).length === 0 && <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No enrollments</td></tr>}
                {(student.enrollments || []).map(en => (
                  <tr key={en.id}>
                    <td style={tdStyle}>{en.enrollment_code}</td>
                    <td style={tdStyle}>{en.cohort?.name || '—'}</td>
                    <td style={tdStyle}><Badge value={en.status} /></td>
                    <td style={tdStyle}>{en.enrollment_date}</td>
                    <td style={tdStyle}><button style={btnSmall} onClick={() => setViewingRegistration(en)}>Details</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button style={btnSecondary} onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
    {viewingRegistration && <AcademyEnrollmentDetails companyId={companyId} enrollment={viewingRegistration} onClose={() => setViewingRegistration(null)} onSaved={async () => { setRegistrationRevision(value => value + 1); await onChanged?.(); }} />}
    </>
  );
}
